import { useEffect, useRef, useState } from 'react'
import { HereMapDefaultLayers } from '@user/components/HereMap/types'
import { useApi } from '@user/components/HereMap/HereMapNew/hooks/useApi'
import { useMapViewAddLeadEnabled } from '@user/store/tenantFeatures/hooks/useMapViewAddLeadEnable'
import { useSelector } from 'react-redux'
import { userGeneralPreferencesList } from '@user/store/userPreferences/userPreferencesGeneral'
import { logger } from '@user/utils/logger'
import {
  getHereMapBaseLayer,
  getPreferredMapBaseView,
} from '@user/components/HereMap/mapView'
import { PestPacLocationDetailsDTO } from '@user/store/map/typings'
import type { RecordAtLocation } from '@user/store/map/typings'
import type { KnockStatusHistoryRecord } from '@user/store/knockStatus/typings'

export interface HereMapClickEvent {
  label?: string
  city?: string
  address?: AddressLocation
  houseNumber?: string
  street?: string
  state?: string
  postalCode?: string
  county?: string
  addressClick?: boolean
  latitude: number
  longitude: number
  entityType?: string
  knockStatusLocationId?: number | null
  knockStatusReason?: string
  knockStatusSettingName?: string
  knockStatusSettingsId?: number | string | null
  knockStatusCreatedTime?: string | null
  knockStatusCreatedByName?: string | null
  knockStatusCount?: number | null
  // SH-1307 -- full history for this coordinate, so "View Knock Status History" can
  // show even when there's no longer an active status (deleted from Settings).
  knockStatusHistory?: KnockStatusHistoryRecord[]
  /** Id of the underlying KnockStatusHistory row (SH-1200). */
  knockStatusHistoryId?: number | null
  contactLocationId?: number
  pestPacDetails?: PestPacLocationDetailsDTO | null
  // SH-1143 — records at this exact coordinate (from the single recordsAtLocation lookup), attached
  // by the click flow so the preview bubble can detect filter-hidden records without a second fetch.
  recordsAtLocation?: RecordAtLocation[]
}

export interface AddressLocation {
  street1?: string
  street2?: string
  city?: string
  stateAbbreviation?: string
  postalCode?: string
  country?: string
  latitude?: number
  longitude?: number
  knockStatusLocationId?: number
}

interface UseHereMapInstanceParams {
  containerRef: React.RefObject<HTMLDivElement>
  center?: H.geo.IPoint
  zoom?: number
  onMapClick?: (data: HereMapClickEvent | undefined) => void
  locationResolved: boolean
}

interface UseHereMapInstanceResult {
  map: H.Map | null
  defaultLayers?: HereMapDefaultLayers
  mounted: boolean
  contactPinsGroup: H.map.Group | null
  knockStatusGroup: H.map.Group | null
  currentLocationGroup: H.map.Group | null
}

const hereApiKey = process.env.REACT_HERE_API_KEY
const minZoomForAddLead =
  Number(process.env.REACT_APP_MAP_MIN_ZOOM_FOR_ADD_LEAD) || 16

export const useHereMapInstance = ({
  containerRef,
  center,
  zoom,
  onMapClick,
  locationResolved,
}: UseHereMapInstanceParams): UseHereMapInstanceResult => {
  const [map, setMap] = useState<H.Map | null>(null)
  const [defaultLayers, setDefaultLayers] = useState<HereMapDefaultLayers>()
  const [mounted, setMounted] = useState(false)
  const [contactPinsGroup, setContactPinsGroup] = useState<H.map.Group | null>(
    null,
  )
  const [knockStatusGroup, setKnockStatusGroup] = useState<H.map.Group | null>(
    null,
  )
  const [currentLocationGroup, setCurrentLocationGroup] =
    useState<H.map.Group | null>(null)

  // Refs to hold latest state/props without triggering re-renders
  const onMapClickRef = useRef(onMapClick)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const platformRef = useRef<H.service.Platform | null>(null)
  const mapEventsRef = useRef<H.mapevents.MapEvents | null>(null)
  const behaviorRef = useRef<H.mapevents.Behavior | null>(null)
  const uiRef = useRef<H.ui.UI | null>(null)
  const scaleBarTimeoutRef = useRef<number | null>(null)

  // Refs for event listeners to ensure clean removal
  const handleMapViewChangeRef = useRef<(() => void) | null>(null)
  const handleTapRef = useRef<((evt: H.mapevents.Event) => void) | null>(null)

  const { getAddressDetailsForMapClick } = useApi()
  const mapViewAddLeadEnabled = useMapViewAddLeadEnabled()
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)
  const preferredMapBaseView = getPreferredMapBaseView(userGeneralPreferences)

  // Keep callback ref updated
  useEffect(() => {
    onMapClickRef.current = onMapClick
  }, [onMapClick])

  // =========================================================
  // 1. INITIALIZATION EFFECT (Runs only once on mount)
  // =========================================================
  useEffect(() => {
    if (!locationResolved || !containerRef.current) return
    if (typeof window === 'undefined' || !(window as any).H) return

    // Guard: Do not initialize if map already exists
    if (map) return

    const container = containerRef.current
    let isMounted = true

    const H = (window as any).H as typeof window.H
    const platform = new H.service.Platform({ apikey: hereApiKey })
    platformRef.current = platform
    const engineType = H.Map.EngineType['HARP']

    const layers = platform.createDefaultLayers({
      engineType: engineType,
      pois: true,
      opt_pois: true,
      lg: 'en',
    }) as HereMapDefaultLayers
    setDefaultLayers(layers)

    const initialBaseLayer = getHereMapBaseLayer(layers, preferredMapBaseView)

    // Initialize map with initial props, but don't bind to them in dependency array
    const mapInstance = new H.Map(container, initialBaseLayer, {
      center: center || { lat: 0, lng: 0 },
      zoom: zoom || 2,
      pixelRatio: window.devicePixelRatio || 1,
      engineType,
    })

    // For satellite view, also add the hybrid overlay
    if (preferredMapBaseView === 'satellite') {
      mapInstance.addLayer(layers.hybrid.day.vector)
    }

    // --- Layer Groups ---
    // Separate groups allow bulk operations per layer without touching unrelated objects.
    const cGroup = new H.map.Group()
    const ksGroup = new H.map.Group()
    const locGroup = new H.map.Group()
    mapInstance.addObject(cGroup)
    mapInstance.addObject(ksGroup)
    mapInstance.addObject(locGroup)

    // --- Event Handlers Definition ---

    const handleMapViewChange = () => {
      if (!isMounted) return
      const currentZoom = mapInstance.getZoom()
      if (currentZoom < 3.5) {
        mapInstance.setZoom(3.5)
      }
    }

    const handleTap = (evt: H.mapevents.Event) => {
      if (!isMounted) return

      const pointer = (evt as any).currentPointer
      if (!pointer) return

      const latLng = mapInstance.screenToGeo(
        pointer.viewportX,
        pointer.viewportY,
      )
      const target = (evt as any).target
      const currentZoom = mapInstance.getZoom() || 0

      // Ignore taps on any marker type — DomMarker (individual pins) or
      // Marker (cluster bubbles). Both must block the add-lead flow.
      if (
        typeof H !== 'undefined' &&
        (H as any).map &&
        (target instanceof (H as any).map.DomMarker ||
          target instanceof (H as any).map.Marker)
      ) {
        return
      }

      if (currentZoom < minZoomForAddLead) {
        logger.info(`Zoom too low for add lead. Current: ${currentZoom}`)
        return
      }

      if (mapViewAddLeadEnabled) {
        getAddressDetailsForMapClick(latLng).then((locationData) => {
          if (!isMounted) return
          if (locationData) {
            onMapClickRef.current?.(locationData)
          }
        })
      }
    }

    // --- Attach Listeners ---

    handleMapViewChangeRef.current = handleMapViewChange
    mapInstance.addEventListener('mapviewchange', handleMapViewChange)

    handleTapRef.current = handleTap
    mapInstance.addEventListener('tap', handleTap)

    // --- Setup Behaviors & UI ---

    const mapEvents = new H.mapevents.MapEvents(mapInstance)
    mapEventsRef.current = mapEvents

    const behavior = new H.mapevents.Behavior(mapEvents)
    behaviorRef.current = behavior
    behavior.enable(H.mapevents.Behavior.Feature.RUBBER_BAND_ZOOM)

    const ui = new H.ui.UI(mapInstance)
    uiRef.current = ui
    ui.setUnitSystem(H.ui.UnitSystem.IMPERIAL)
    const scaleBar = new H.ui.ScaleBar({ alignment: 'bottom-right' })
    ui.addControl('scalebar', scaleBar)

    // Adjust scalebar position
    const adjustScaleBarPosition = () => {
      if (!isMounted) return
      const node = container.querySelector('.H_scalebar') as HTMLElement | null
      if (node) node.style.right = '56px'
    }
    scaleBarTimeoutRef.current = window.setTimeout(adjustScaleBarPosition, 100)

    // Resize Observer — debounced so layout animations (e.g. drawer open) finish
    // before the WebGL canvas resizes, preventing a mid-animation blank flash.
    let resizeTimer: number | null = null
    const scheduleResize = () => {
      if (!isMounted) return
      if (resizeTimer !== null) window.clearTimeout(resizeTimer)
      resizeTimer = window.setTimeout(() => {
        resizeTimer = null
        if (isMounted) mapInstance.getViewPort().resize()
      }, 100)
    }

    const resizeObserver = new ResizeObserver(scheduleResize)
    resizeObserver.observe(container)
    resizeObserverRef.current = resizeObserver

    const handleWindowResize = scheduleResize
    window.addEventListener('resize', handleWindowResize)

    const DRAG_THRESHOLD_PX = 5
    let pendingPointerId: number | null = null
    let dragOrigin: { x: number; y: number } | null = null

    const handleContainerPointerDown = (e: PointerEvent) => {
      if (e.button === 0 && e.isPrimary) {
        pendingPointerId = e.pointerId
        dragOrigin = { x: e.clientX, y: e.clientY }
      }
    }

    const handleContainerPointerMove = (e: PointerEvent) => {
      if (
        pendingPointerId === null ||
        e.pointerId !== pendingPointerId ||
        !dragOrigin
      )
        return

      const dx = e.clientX - dragOrigin.x
      const dy = e.clientY - dragOrigin.y
      if (dx * dx + dy * dy > DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) {
        try {
          container.setPointerCapture(pendingPointerId)
        } catch {
          // Ignore – capture can fail if the pointer is already held
        }
        pendingPointerId = null
        dragOrigin = null
      }
    }

    const handleContainerPointerUp = () => {
      pendingPointerId = null
      dragOrigin = null
    }

    container.addEventListener('pointerdown', handleContainerPointerDown)
    container.addEventListener('pointermove', handleContainerPointerMove)
    container.addEventListener('pointerup', handleContainerPointerUp)

    const handleWindowBlur = () => {
      const b = behaviorRef.current
      if (b) {
        b.disable()
        b.enable()
        b.enable(H.mapevents.Behavior.Feature.RUBBER_BAND_ZOOM)
      }
    }

    window.addEventListener('blur', handleWindowBlur)

    // Finalize state
    setMap(mapInstance)
    setMounted(true)
    setContactPinsGroup(cGroup)
    setKnockStatusGroup(ksGroup)
    setCurrentLocationGroup(locGroup)

    // --- Cleanup Function ---
    return () => {
      isMounted = false

      if (scaleBarTimeoutRef.current) {
        window.clearTimeout(scaleBarTimeoutRef.current)
        scaleBarTimeoutRef.current = null
      }

      if (resizeTimer !== null) {
        window.clearTimeout(resizeTimer)
        resizeTimer = null
      }

      window.removeEventListener('resize', handleWindowResize)
      window.removeEventListener('blur', handleWindowBlur)
      container.removeEventListener('pointerdown', handleContainerPointerDown)
      container.removeEventListener('pointermove', handleContainerPointerMove)
      container.removeEventListener('pointerup', handleContainerPointerUp)

      if (handleMapViewChangeRef.current) {
        mapInstance.removeEventListener(
          'mapviewchange',
          handleMapViewChangeRef.current,
        )
      }
      if (handleTapRef.current) {
        mapInstance.removeEventListener('tap', handleTapRef.current)
      }

      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
      }

      // Explicitly remove all objects to help GC.
      // Markers may live directly on the map (clusters, legacy) or inside a group.
      const objects = mapInstance.getObjects()
      objects.forEach((obj: any) => {
        if (obj instanceof H.map.Group) {
          obj.getObjects().forEach((child: any) => {
            const data = child.getData?.()
            data?.__cleanup?.()
            data?.clearEventListener?.()
          })
        } else if (
          obj instanceof H.map.DomMarker ||
          obj instanceof H.map.Marker
        ) {
          const data = obj.getData?.()
          data?.__cleanup?.()
          data?.clearEventListener?.()
        }
      })
      mapInstance.removeObjects(objects)

      // Dispose components in order
      behaviorRef.current?.disable()
      behaviorRef.current?.dispose()
      mapEventsRef.current?.dispose()
      uiRef.current?.dispose()

      mapInstance.dispose()
      platformRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationResolved, containerRef])
  // ^ removed 'center' and 'zoom' to prevent re-initialization loops

  // =========================================================
  // 2. UPDATE EFFECTS (Handle prop changes efficiently)
  // =========================================================

  // Update Center Only
  useEffect(() => {
    if (!map || !center) return
    // Optional: Add a check if the center actually moved significantly to avoid jitter
    map.setCenter(center)
  }, [map, center])

  // Update Zoom Only
  useEffect(() => {
    if (!map || typeof zoom !== 'number') return
    map.setZoom(zoom)
  }, [map, zoom])

  return {
    map,
    defaultLayers,
    mounted,
    contactPinsGroup,
    knockStatusGroup,
    currentLocationGroup,
  }
}
